import { useState } from "react";
import { useAuthStore } from "@/lib/store/authStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordModal({ open, onOpenChange }: ChangePasswordModalProps) {
  const frappeClient = useAuthStore((state) => state.frappeClient);
  const frappeUrl = useAuthStore((state) => state.frappeUrl);
  const [newPassword, setNewPassword] = useState("");
  const [logoutAllSessions, setLogoutAllSessions] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (!frappeClient || !frappeUrl) {
      setError("Not authenticated. Please log in.");
      return;
    }

    setIsChanging(true);

    try {
      const baseUrl = frappeUrl.replace(/\/$/, "");
      const updatePasswordUrl = `${baseUrl}/api/method/frappe.core.doctype.user.user.update_password`;

      const formData = new URLSearchParams();
      formData.append("new_password", newPassword);
      formData.append("logout_all_sessions", logoutAllSessions ? "1" : "0");

      const response = await fetch(updatePasswordUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
        },
        credentials: "include",
        body: formData.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText,
        }));
        throw new Error(errorData.message || errorData.exc || "Failed to change password");
      }

      setSuccess(true);
      setNewPassword("");
      setLogoutAllSessions(false);

      // Close modal after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient with blur */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/80 to-transparent dark:from-gray-900 dark:via-gray-900/80 backdrop-blur-sm pointer-events-none" />

        {/* Checkmark button - top right */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isChanging || !newPassword}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] active:scale-95 shadow-apple-sm",
            "transition-all duration-300 ease-out",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
        >
          <Check className="w-5 h-5 text-white transition-transform duration-300" />
          <span className="sr-only">Save</span>
        </Button>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                Change Password
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                Set a new password for your account
              </DialogDescription>
            </DialogHeader>

            <div className="pb-8 px-10 pt-2">
              {success && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Password changed successfully!
                  </p>
                </div>
              )}

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-gray-950 rounded-md border border-gray-200/40 dark:border-gray-800/40">
                <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/50 dark:border-gray-800/50">
                  <Label
                    htmlFor="new_password"
                    className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0"
                  >
                    New Password
                  </Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 max-w-[60%] text-right bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 rounded-none placeholder:text-gray-400"
                    placeholder="Enter new password"
                    disabled={isChanging}
                  />
                </div>

                <div className="flex items-center justify-between min-h-[52px] px-5">
                  <Label
                    htmlFor="logout_all_sessions"
                    className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0"
                  >
                    Logout From All Devices After Changing Password
                  </Label>
                  <input
                    id="logout_all_sessions"
                    type="checkbox"
                    checked={logoutAllSessions}
                    onChange={(e) => setLogoutAllSessions(e.target.checked)}
                    className="w-5 h-5 rounded-md border-gray-300 dark:border-gray-700 text-[#007AFF] focus:ring-[#007AFF] focus:ring-2 focus:ring-offset-0 cursor-pointer"
                    disabled={isChanging}
                  />
                </div>
              </div>
            </div>

            {/* Bottom fade-out gradient with blur */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/80 to-transparent dark:from-gray-900 dark:via-gray-900/80 backdrop-blur-sm pointer-events-none" />
          </div>

          {/* Sticky Save Button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit"
              disabled={isChanging || !newPassword}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] active:scale-[0.98] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isChanging ? "Changing Password..." : "Change Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

